import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import * as React from "react";

import { FileRecord } from "@/shared/domain/files";
import { pluralize } from "@/shared/text";

import { RobotoButton } from "../RobotoButton";
import { RobotoDivider } from "../RobotoDivider";
import { RobotoTypography } from "../RobotoTypography";

import { DatasetView } from "./DatasetView";
import styles from "./FilePicker.module.css";
import { FileView } from "./FileView";
import { useFilePicker } from "./useFilePicker";

interface FilePickerProps {
  /** Organization ID to fetch datasets from */
  orgId: string;

  /** Whether the picker modal is open */
  open: boolean;

  /** Callback when the picker is closed/cancelled */
  onClose: () => void;

  /** Callback when files are selected and confirmed */
  onSubmit: (files: FileRecord[]) => void;

  /** Optional: Title for the picker */
  title?: string;

  /** Optional: Active dataset IDs from current workspace to display at top */
  activeDatasetIds?: string[];

  /** Optional: File IDs that are already selected and should be non-selectable */
  existingFileIds?: string[];

  /** Optional: Gitignore-style glob patterns to restrict which files are shown */
  includePatterns?: string[];
}

export function FilePicker(props: FilePickerProps) {
  const {
    orgId,
    open,
    onClose,
    onSubmit,
    title,
    activeDatasetIds,
    existingFileIds,
    includePatterns,
  } = props;

  const {
    activeSource,
    numFilesSelected,
    clearAllSelections,
    isFileSelected,
    isFileAlreadyAdded,
    getSelectedFiles,
    getSelectedCountForDataset,
    resetState,
    setActiveSource,
    toggleFileSelection,
  } = useFilePicker(existingFileIds);

  const handleDatasetSelect = React.useCallback(
    (datasetId: string) => {
      setActiveSource({ type: "dataset", id: datasetId });
    },
    [setActiveSource],
  );

  const handleSubmit = () => {
    const selectedFiles = getSelectedFiles();
    onSubmit(selectedFiles);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: styles.dialogPaper,
      }}
      TransitionProps={{
        // Reset state after the dialog has exited
        onExited: resetState,
      }}
    >
      <DialogTitle className={styles.titleContainer}>
        <div>
          <h1 className={styles.titleText}>{title ?? "Select files"}</h1>
          <RobotoTypography
            className={styles.subtitleText}
            color="text.secondary"
          >
            {numFilesSelected > 0
              ? `${numFilesSelected} ${pluralize("file", numFilesSelected)} selected`
              : "No files selected"}
          </RobotoTypography>
        </div>
        <RobotoButton
          onClick={clearAllSelections}
          disabled={numFilesSelected === 0}
          eventName={"FilePickerClearAllClicked"}
          eventProperties={{ fileCount: numFilesSelected }}
        >
          Clear
        </RobotoButton>
      </DialogTitle>

      <RobotoDivider />

      <DialogContent className={styles.dialogContent}>
        <div className={styles.leftPanel}>
          <DatasetView
            activeDatasetId={
              activeSource?.type === "dataset" ? activeSource.id : null
            }
            orgId={orgId}
            onDatasetSelect={handleDatasetSelect}
            getSelectedCountForDataset={getSelectedCountForDataset}
            activeDatasetIds={activeDatasetIds}
          />
        </div>
        <div className={styles.rightPanel}>
          <FileView
            activeSource={activeSource}
            isFileSelected={isFileSelected}
            isFileAlreadyAdded={isFileAlreadyAdded}
            onFileToggle={toggleFileSelection}
            includePatterns={includePatterns}
          />
        </div>
      </DialogContent>

      <RobotoDivider />

      <DialogActions>
        <RobotoButton onClick={onClose} eventName={"FilePickerCanceled"}>
          Cancel
        </RobotoButton>
        <RobotoButton
          onClick={handleSubmit}
          variant="contained"
          disabled={numFilesSelected === 0}
          eventName={"FilePickerSubmitted"}
          eventProperties={{ fileCount: numFilesSelected }}
        >
          Add {numFilesSelected > 0 ? `(${numFilesSelected})` : ""}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}
